import styles from './GeofenceTooltip.module.scss';
import { useTranslation } from 'react-i18next';
import {
  useGetGeofenceAreasQuery,
  useGetVehicleGroupsQuery,
} from '@customer/store/apis/metadataApi';
import { useMemo } from 'react';
import { Tooltip } from 'react-leaflet';
import { FenceType } from '@customer/store/apis/geofencesApi';
import { GeofenceProperties } from '@customer/store/apis/interactionsApi';

export interface GeofenceTooltipProps {
  properties?: GeofenceProperties;
}

interface DisplayItem {
  title: string;
  value: string;
}

export function GeofenceTooltip({ properties }: GeofenceTooltipProps) {
  const { t } = useTranslation();

  const { data: geofenceAreas } = useGetGeofenceAreasQuery(undefined, {
    refetchOnMountOrArgChange: 1800,
  });
  const { data: assetGroups } = useGetVehicleGroupsQuery(undefined, {
    refetchOnMountOrArgChange: 1800,
  });

  const displayItems = useMemo<DisplayItem[]>(() => {
    if (!properties) {
      return [];
    }

    const { name, fenceType, slope, areas, ags } = properties;

    // Remove any null values
    const areaIds: number[] =
      areas
        ?.split(',')
        ?.map((a: string) => parseInt(a))
        ?.filter(Boolean) ?? [];
    const assetGroupIds: number[] =
      ags
        ?.split(',')
        ?.map((a: string) => parseInt(a))
        ?.filter(Boolean) ?? [];

    const displayAreas =
      geofenceAreas && areaIds.length > 0
        ? geofenceAreas
            .filter(a => areaIds.includes(a.id))
            .map(a => a.n)
            .join(', ')
        : t('None');
    const displayGroups =
      assetGroups && assetGroupIds.length > 0
        ? assetGroups
            .filter(a => assetGroupIds.includes(a.id))
            .map(a => a.n)
            .join(', ')
        : t('None');

    const fenceTypes: string[] = [];

    if (fenceType === FenceType.Intersection) {
      fenceTypes.push(t('GeofenceFenceType.Intersection'));
    } else if (fenceType === FenceType.WetRoad) {
      fenceTypes.push(t('GeofenceFenceType.WetRoad'));
    }

    if (slope === true) {
      fenceTypes.push(t('GeofenceFenceType.Slope'));
    }

    const sortedFenceTypes =
      fenceTypes.length > 0
        ? fenceTypes.sort((a, b) => a.localeCompare(b)).join(', ')
        : t('GeofenceFenceType.Standard');

    return [
      { title: t('Name'), value: name },
      { title: t('GenericTypes'), value: sortedFenceTypes },
      { title: t('Areas'), value: displayAreas },
      { title: t('AssetGroups'), value: displayGroups },
    ];
  }, [properties, assetGroups, geofenceAreas, t]);

  return (
    <Tooltip className={styles.container} direction="right" sticky>
      {displayItems.map((item, index) => (
        <div key={index} className={styles.text}>
          <b>{item.title}: </b>
          {item.value}
        </div>
      ))}
    </Tooltip>
  );
}

export default GeofenceTooltip;
